import { apiJson, apiMultipart } from "../utils/api.js";
import { handleApiError } from "../utils/errorHandler.js";

// Module-level cache and fetch state to prevent duplicate API calls
let chefDataCache = null;
let isFetchingChefData = false;
let fetchPromise = null;

// Module-level cache for chef labels to prevent duplicate API calls
// Cache includes timestamp to allow refetching after a certain time
// Short duration (10 seconds) to prevent duplicate calls from React StrictMode
// but allow refetching when navigating back to the page
let chefLabelsCache = null;
let chefLabelsCacheTimestamp = null;
let isFetchingChefLabels = false;
let fetchLabelsPromise = null;
const CHEF_LABELS_CACHE_DURATION = 10 * 1000; // 10 seconds - only prevents duplicate calls in same mount cycle

/**
 * Get chef data with caching and deduplication
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<Object>} Chef data
 */
export const getChefData = async (forceRefresh = false) => {
    // Return cached data if available and not forcing refresh
    if (chefDataCache && !forceRefresh) {
        return chefDataCache;
    }

    // If already fetching, return the existing promise
    if (isFetchingChefData && fetchPromise) {
        return fetchPromise;
    }

    // Start new fetch
    isFetchingChefData = true;
    fetchPromise = (async () => {
        try {
            const response = await apiJson.get("/kms/chef/me");
            chefDataCache = response.data;
            return response.data;
        } catch (error) {
            // Clear cache on auth error
            if (error.response?.status >= 400 && error.response?.status < 500) {
                chefDataCache = null;
            }
            
            handleApiError(error, {
                defaultMessage: "Error: 3001: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
            });
            throw error;
        } finally {
            isFetchingChefData = false;
            fetchPromise = null;
        }
    })();

    return fetchPromise;
};

/**
 * Get chef labels with caching and deduplication
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<Array>} Chef labels
 */
export const getChefLabels = async (forceRefresh = false) => {
    const now = Date.now();
    
    // Check if cache is still valid (not expired and not forcing refresh)
    const isCacheValid = chefLabelsCache && 
                        chefLabelsCacheTimestamp && 
                        (now - chefLabelsCacheTimestamp) < CHEF_LABELS_CACHE_DURATION &&
                        !forceRefresh;
    
    // Return cached data if available and valid
    if (isCacheValid) {
        return chefLabelsCache;
    }

    // If already fetching, return the existing promise
    if (isFetchingChefLabels && fetchLabelsPromise) {
        return fetchLabelsPromise;
    }

    // Start new fetch
    isFetchingChefLabels = true;
    fetchLabelsPromise = (async () => {
        try {
            const response = await apiJson.get("/data/chef-labels");
            chefLabelsCache = response.data;
            chefLabelsCacheTimestamp = Date.now();
            return response.data;
        } catch (error) {
            // Clear cache on error
            chefLabelsCache = null;
            chefLabelsCacheTimestamp = null;
            handleApiError(error, {
                defaultMessage: "Error: 3002: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
            });
            throw error;
        } finally {
            isFetchingChefLabels = false;
            fetchLabelsPromise = null;
        }
    })();

    return fetchLabelsPromise;
};

/**
 * Login with OTP
 * @param {string} values - OTP code
 * @returns {Promise<Object>} Login response with token
 */
export const getLoginData = async (values) => {
    try {
        const response = await apiJson.post("/kms/auth/login", {
            otp: values
        });

        if (response.data.token) {
            localStorage.setItem('authToken', response.data.token);
        }

        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3003: Ներեցեք, տրամադրված մեկանգամյա կոդը սխալ է: Խնդրում ենք ստուգել և փորձել կրկին:"
        });
        throw error;
    }
};

/**
 * Update chef status
 * @param {string} id - Chef ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated chef data
 */
export const updateChefStatus = async (id, status) => {
    try {
        const response = await apiJson.put(`/kms/chef/${id}/status`, null, {
            params: {
                status: status
            }
        });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3004: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        throw error;
    }
};

/**
 * Update chef profile
 * @param {Object} chefData - Chef data to update
 * @param {string} id - Chef ID
 * @param {File} file - Optional profile image file
 * @returns {Promise<Object>} Updated chef data
 */
export const updateChef = async (chefData, id, file) => {
    try {
        const formData = new FormData();

        // Build updateDto according to backend schema
        // Only include fields that exist in ChefUpdateDto schema
        const updateDto = {};
        
        // Optional: chefLabelDtos
        if (chefData.chefLabelDtos && Array.isArray(chefData.chefLabelDtos) && chefData.chefLabelDtos.length > 0) {
            updateDto.chefLabelDtos = chefData.chefLabelDtos.map((label) => ({
                id: label.id,
                name: label.name,
                chefLabelTranslationSet: label.chefLabelTranslationSet?.map((translation) => ({
                    languageCode: translation.languageCode,
                    value: translation.value,
                })) || []
            }));
        }
        
        // Optional: socialLinks
        if (chefData.socialLinkDtoList && Array.isArray(chefData.socialLinkDtoList)) {
            updateDto.socialLinks = chefData.socialLinkDtoList.map((link) => ({
                id: link.id || null,
                link: link.link || '',
                socialType: link.socialType
            }));
        }
        
        // Optional: email
        if (chefData.email !== undefined && chefData.email !== null) {
            updateDto.email = chefData.email;
        }
        
        // Optional: phoneNumber
        if (chefData.phoneNumber !== undefined && chefData.phoneNumber !== null) {
            updateDto.phoneNumber = chefData.phoneNumber;
        }
        
        // Optional: kitchenAm (only Am, not Ru/En)
        if (chefData.kitchenAm !== undefined && chefData.kitchenAm !== null) {
            updateDto.kitchenAm = chefData.kitchenAm;
        }
        
        // Optional: descriptionAm (only Am, not Ru/En)
        if (chefData.descriptionAm !== undefined && chefData.descriptionAm !== null) {
            updateDto.descriptionAm = chefData.descriptionAm;
        }
        
        // Optional: fullNameAm
        if (chefData.fullNameAm !== undefined && chefData.fullNameAm !== null) {
            updateDto.fullNameAm = chefData.fullNameAm;
        }
        
        // Optional: username
        if (chefData.username !== undefined && chefData.username !== null) {
            updateDto.username = chefData.username;
        }
        
        // Optional: telegramId (comma-separated string from array)
        // Convert array to comma-separated string, send null if empty (backend will handle normalization)
        if (chefData.telegramId !== undefined && chefData.telegramId !== null) {
            // If it's an array, convert to comma-separated string
            if (Array.isArray(chefData.telegramId)) {
                // Filter out empty/whitespace-only IDs and trim each ID
                const filteredIds = chefData.telegramId
                    .map(id => id ? id.toString().trim() : '')
                    .filter(id => id.length > 0);
                // Only include if we have valid IDs, otherwise omit (backend expects null or string)
                if (filteredIds.length > 0) {
                    updateDto.telegramId = filteredIds.join(',');
                }
                // If no valid IDs, don't include the field (backend will keep existing value or set to null)
            } else {
                // Already a string, trim it and include only if non-empty
                const trimmed = chefData.telegramId.toString().trim();
                if (trimmed.length > 0) {
                    updateDto.telegramId = trimmed;
                }
            }
        }
        // If not provided or empty, don't include in updateDto (backend will keep existing value)
        
        // Optional: chefLegalDto with address
        if (chefData.address && typeof chefData.address === 'object') {
            const address = chefData.address;
            const addressDto = {};
            
            // Only include address fields that have values or need to be cleared
            // Map frontend fields to API fields:
            // - residence -> city
            // - domofon -> intercom
            // - coordinates, latitude, longitude -> coordinates [lat, lng]
            
            if (address.country !== undefined) {
                addressDto.country = address.country || "";
            }
            if (address.region !== undefined) {
                addressDto.region = address.region || "";
            }
            if (address.residence !== undefined) {
                addressDto.city = address.residence || ""; // Map residence to city
            }
            if (address.street !== undefined) {
                addressDto.street = address.street || "";
            }
            if (address.home !== undefined) {
                addressDto.home = address.home || "";
            }
            if (address.floor !== undefined) {
                addressDto.floor = address.floor || "";
            }
            if (address.flat !== undefined) {
                addressDto.flat = address.flat || "";
            }
            if (address.entrance !== undefined) {
                addressDto.entrance = address.entrance || "";
            }
            if (address.domofon !== undefined) {
                addressDto.intercom = address.domofon || ""; // Map domofon to intercom
            }
            
            // Handle coordinates - prioritize coordinates array, then latitude/longitude
            // Backend expects [lng, lat] format (longitude first, then latitude)
            if (address.coordinates !== undefined) {
                if (Array.isArray(address.coordinates) && address.coordinates.length >= 2) {
                    // Swap order from [lat, lng] to [lng, lat] for backend
                    addressDto.coordinates = [address.coordinates[1], address.coordinates[0]];
                } else if (address.latitude !== undefined && address.longitude !== undefined) {
                    // Convert from separate lat/lng to array format [lng, lat]
                    const lat = typeof address.latitude === 'number' ? address.latitude : parseFloat(address.latitude);
                    const lng = typeof address.longitude === 'number' ? address.longitude : parseFloat(address.longitude);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        addressDto.coordinates = [lng, lat];
                    }
                } else if (address.coordinates === null || address.coordinates === "") {
                    // Clear coordinates
                    addressDto.coordinates = null;
                }
            } else if (address.latitude !== undefined && address.longitude !== undefined) {
                // If coordinates array not provided but lat/lng are, convert to array [lng, lat]
                const lat = typeof address.latitude === 'number' ? address.latitude : parseFloat(address.latitude);
                const lng = typeof address.longitude === 'number' ? address.longitude : parseFloat(address.longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                    addressDto.coordinates = [lng, lat];
                }
            }
            
            // Only add chefLegalDto if address has at least one field
            if (Object.keys(addressDto).length > 0) {
                updateDto.chefLegalDto = {
                    address: addressDto
                };
            }
        }
        
        formData.append("updateDto", JSON.stringify(updateDto));

        // Only append file if it's a valid File object with content
        if (file && file instanceof File && file.size > 0) {
            formData.append("file", file);
        }

        const response = await apiMultipart.put(`/kms/chef/${id}`, formData);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3004: Չհաջողվեց թարմացնել ձեր տվյալները սխալ մուտքագրված տվյալների պատճառով: Խնդրում ենք վերանայել մուտքագրված տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին"
        });
        throw error;
    }
};

/**
 * Upload chef banner
 * @param {string} id - Chef ID
 * @param {File} file - Banner image file
 * @returns {Promise<Object>} Updated chef data
 */
export const uploadChefBanner = async (id, file) => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await apiMultipart.post(`/kms/chef/${id}/banner`, formData);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3005: Չհաջողվեց վերբեռնել բանները: Խնդրում ենք փորձել կրկին"
        });
        throw error;
    }
};

/**
 * Delete chef banner
 * @param {string} id - Chef ID
 * @returns {Promise<void>}
 */
export const deleteChefBanner = async (id) => {
    try {
        await apiJson.delete(`/kms/chef/${id}/banner`);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3006: Չհաջողվեց ջնջել բանները: Խնդրում ենք փորձել կրկին"
        });
        throw error;
    }
};



